import React, { useEffect, useMemo, useState } from "react";
import { TgkParameterMetadata } from "../../../api/models/tgk-parameter-metadata";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/hooks";
import { selectCpmUsername } from "../../../redux/feature/settings/configurations-slice";
import {
  CoObjectAdvanced,
  CoObjectGeneralInfo,
  CoObjectItem,
  SheetForm,
  TgkAmountObjectSource,
  TgkDimensionAttributeSource,
  TgkDropDownSource,
  TgkTableObjectSource,
  TgkTextSource,
} from "../../interfaces/CoObject";
import NotificationManager from "../../../common/notification/NotificationManager";
import {
  descriptionLanguage,
  formatMessage,
  formatMessageDefault,
  formatMessageWithParams,
} from "../../../utils/helpers/LanguageHelper";
import { editTitleAndTag } from "../../../utils/helpers/ContentControlHelper";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import LoadingManager from "../../../common/loading/LoadingManager";
import {
  EDIT_OBJECT_NOTIFICATION,
  TABLE_OBJECT_PARAMETERS,
  TGK_TEXT_OBJECT_DATASET_FIELDS,
} from "../../../utils/constants/notification";
import { fetchParametersMetadata } from "../../../api/object/Parameters";
import { store } from "../../../redux/store/store";
import { deleteObjectAsync, lockObject } from "../../../redux/feature/cpm/cpm-slice";
import { invalidate } from "../../../redux/feature/validation/validation-slice";
import { mapCoObjectToRefreshElaborationItem } from "../../../redux/feature/cpm/map-objects";
import { createElaborationRequestAsync } from "../../../redux/feature/object/remote-elaborations-slice";
import { CustomModalProps } from "../../layout/ActionModal";
import {
  ObjectPanelPivotProps,
  ObjectPanelTabs,
  ObjectPanelValidator,
  TableUpdateStrategy,
} from "./ObjectPanelComponent";
import * as uuid from "uuid";
import { fetchFileExist } from "../../../api/object/ExcelFile";
import { DatasetField } from "../../../api/models/dataset-field";
import { extractMandatoryDimensionsFromDatasetFields, fetchDatasetFields } from "../../../api/object/DatasetField";
import { CoObjectTypeEnum, ElaborationRequest } from "../../../api/models";
import { moveCursorToContentControlId } from "../../../office/WordDocumentPart";
import { navigate } from "../../../redux/feature/general/navigation-slice";
import { EDIT_OBJECT } from "../../../utils/constants/navigation";
import { isAddNewPossible, isCursorInsideTable } from "../../../office/ContentControl";
import { DimensionDetail } from "../../../api/models/dimension-detail";
import { DimensionParameter } from "../../../api/models/dimension-parameter";
import { Scenario } from "../../../api/models/scenario";
import { ComponentReference } from "../../../api/models/component-reference";
import {
  applyComponentData,
  isComponentInstance,
  validateComponentData,
} from "../../../utils/helpers/ComponentInstanceHelper";
import { CoObjectContext } from "../ObjectPanelElaborationWrapper";
import {
  applyInheritedInfo,
  hasInheritMoreThanOneValueViolation,
  notifyInheritMoreThanOneValue,
} from "../../../utils/helpers/InheritanceHelper";
import AsyncObjectUpdateManager from "../../../common/update/AsyncObjectUpdateManager";
import { Dimension } from "../../../api/models/dimension";
import { fetchDimensions } from "../../../api/object/Dimensions";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";
import {
  fetchReferringScenario,
  notifyInvalidReferenceScenario,
  validateRefScenario,
} from "../../../utils/helpers/ReferenceScenarioHelper";
import { coObjectIsWithoutDimensions, coObjectIsWithoutParameters } from "../../../utils/helpers/ObjectCheckerHelper";
import { isFeatureActiveOnCurrentClient } from "../../../api/config/ExternalConfig";

const initialStateTabs: ObjectPanelTabs[] = [
  {
    key: "0",
    name: "cof.documentproperties.generalinfo",
    isValid: false,
  },
];

const initialState: ObjectPanelState = {
  tabs: initialStateTabs,
  sourceEdited: false,
  selectedKey: 0,
  tableUpdateType: "update",
};

export interface ObjectPanelState {
  tabs: ObjectPanelTabs[];
  sourceEdited: boolean;
  //TODO: To be renamed to "elaborate"
  selectedKey: number;
  tableUpdateType: TableUpdateStrategy["typeUpdate"];
}

export const getInitialTabs = (isEdit: boolean): ObjectPanelTabs[] => [
  {
    key: "0",
    name: "cof.documentproperties.generalinfo",
    isValid: isEdit,
  },
];

const addSourceTab = (tabs: ObjectPanelTabs[], isEdit: boolean, coObject: CoObjectItem): void => {
  tabs.push({
    key: "1",
    name: "cof.createnewobject.source",
    isValid: isEdit || isComponentInstance(coObject),
  });
};

const addParametersTab = (
  tabs: ObjectPanelTabs[],
  type: string,
  isEdit: boolean,
  coObject: CoObjectItem,
  sourceTable?: TgkTableObjectSource
): void => {
  if (
    ((isEdit || isComponentInstance(coObject)) && coObject?.source?.type === "AMOUNT_AIH") ||
    ((type === "TABLE" || type === "CHART") && sourceTable?.objectSource !== SourceTypeEnum.EXCEL) ||
    (type === "AMOUNT" && sourceTable?.objectSource === SourceTypeEnum.TAGETIK_FORM) ||
    type === "TGK_TEXT"
  ) {
    tabs.push({
      key: "2",
      name: "cof.createnewobject.parameters",
      isValid: isEdit || isComponentInstance(coObject),
    });
  }
};

const addAdvancedTab = (
  tabs: ObjectPanelTabs[],
  type: string,
  isEdit: boolean,
  coObject: CoObjectItem,
  sourceTable?: TgkTableObjectSource
): void => {
  if (
    (type === "TABLE" || type === "CHART" || type === "AMOUNT") &&
    (coObject.advanced || !isEdit || isComponentInstance(coObject))
  ) {
    tabs.push({
      key:
        type === "TABLE" ||
        type === "CHART" ||
        (type === "AMOUNT" && sourceTable?.objectSource === SourceTypeEnum.TAGETIK_FORM) ||
        coObject?.source?.type === "AMOUNT_AIH"
          ? "3"
          : "2",
      name: "cof.createnewobject.advanced",
      isValid: isEdit || isComponentInstance(coObject),
    });
  }
};

const notifyIfMissingParameters = (coObject: CoObjectItem, mandatoryMetadata: TgkParameterMetadata[]) => {
  if (!hasAllMandatoryParameters(mandatoryMetadata, coObject.parameters)) {
    NotificationManager.addNotification({
      title: "",
      message: formatMessage("cof.createnewobject.inheritparameters.error"),
      type: "warning",
      isTemporary: false,
    });
  }
};

const hasAllMandatoryParameters = (
  mandatoryMetadata: TgkParameterMetadata[],
  objectParameters: DimensionDetail[] | DimensionParameter[]
) => {
  if (!mandatoryMetadata?.length) return true;
  if (!objectParameters) return false;

  return mandatoryMetadata
    .filter((m) => m.type === "dimension")
    .every((m) => (objectParameters as DimensionDetail[]).some((p) => p.structureCode === m.dimensionCode));
};

const initTabsByType = (type: string, isEdit: boolean, coObject: CoObjectItem): ObjectPanelTabs[] => {
  const sourceTable = coObject.source as TgkTableObjectSource;
  let tabs = getInitialTabs(isEdit);

  const sourceTypes = ["DROPDOWN_LIST", "TABLE", "AMOUNT", "AMOUNT_AIH", "DIMENSION_ATTRIBUTE", "CHART", "TGK_TEXT"];
  if (sourceTypes.includes(type)) {
    addSourceTab(tabs, isEdit, coObject);
  }

  addParametersTab(tabs, type, isEdit, coObject, sourceTable);
  addAdvancedTab(tabs, type, isEdit, coObject, sourceTable);

  return tabs;
};

export interface AvailableParameters {
  mandatoryParametersMetadata: TgkParameterMetadata[];
  optionalParametersMetadata: TgkParameterMetadata[];
}

export interface AvailableDimensions {
  mandatoryDimensionsMetadata: Dimension[];
  optionalDimensionsMetadata: Dimension[];
}

const useObjectPanelComponent = (props: ObjectPanelPivotProps) => {
  const coObjectContext = React.useContext(CoObjectContext);
  const [state, setState] = React.useState<ObjectPanelState>(initialState);
  const [originalSheetArea, setOriginalSheetArea] = React.useState<SheetForm>({ uid: "", desc: "", code: "" });
  const [availableParametersMetadata, setAvailableParametersMetadata] = React.useState<
    AvailableParameters & { dbId?: string; formCode?: string }
  >({
    mandatoryParametersMetadata: [],
    optionalParametersMetadata: [],
  });
  const [availableDimensionsMetadata, setAvailableDimensionsMetadata] = React.useState<AvailableDimensions>({
    mandatoryDimensionsMetadata: [],
    optionalDimensionsMetadata: [],
  });
  const [datasetFields, setDatasetFields] = React.useState<DatasetField[]>([]);
  const { isEdit, coObject, documentPart, documentPartMetadata, onSaveBtn, validator, isLocked } = props;
  const [pasteMode, setPasteMode] = React.useState<boolean>(false);
  const { selectedKey, tabs } = state;
  const selectedTab = useMemo(() => tabs.find((t) => String(selectedKey) == t.key), [tabs, selectedKey]);
  const username = useAppSelector(selectCpmUsername);
  const [isFileExcelExisting, setIsFileExcelExisting] = useState<boolean>(true);
  const [fileExcelName, setFileExcelName] = useState<string>("");
  const [isOldSheetOrAreaSelected, setIsOldSheetOrAreaSelected] = useState<boolean>(false);
  const [isLoadingSheetAndArea, setIsLoadingSheetAndArea] = useState<boolean>(false);
  const [isSheetChartAndAreaToHide, setIsSheetChartAndAreaToHide] = useState(false);
  const amountSource = coObject.source as TgkAmountObjectSource;
  const isFormAmount =
    coObject.info?.objectType === CoObjectTypeEnum.AMOUNT &&
    amountSource?.type !== "AMOUNT_AIH" &&
    amountSource?.objectSource === SourceTypeEnum.TAGETIK_FORM;
  const formParametersMetadata = useMemo(() => {
    if (
      !isFormAmount ||
      availableParametersMetadata.dbId !== amountSource?.dbApplication?.id ||
      availableParametersMetadata.formCode !== amountSource?.form?.code
    ) {
      return undefined;
    }
    return [
      ...availableParametersMetadata.mandatoryParametersMetadata,
      ...availableParametersMetadata.optionalParametersMetadata,
    ];
  }, [availableParametersMetadata, isFormAmount, amountSource?.dbApplication?.id, amountSource?.form?.code]);

  useEffect(() => {
    AsyncObjectUpdateManager.addAsyncObjectUpdateListener(handleAsyncObjectUpdate);
    return () => {
      AsyncObjectUpdateManager.removeAsyncObjectUpdateListener(handleAsyncObjectUpdate);
    };
  }, []);

  const handleAsyncObjectUpdate = async (coObject: CoObjectItem) => {
    await applyInheritedInfo(coObject, documentPartMetadata);
    coObjectContext.update({ ...coObject });
  };

  useEffect(() => {
    if (!coObject) return; // Add this check to prevent errors
    setState((s) => ({
      ...s,
      tabs: props.coObject?.info ? initTabsByType(props.coObject.info.objectType, isEdit, coObject) : initialStateTabs,
      sourceEdited: false,
      selectedKey: 0,
      tableUpdateType: "update",
    }));
    checkFileExcelExists();
  }, [coObject.contentControlId]);

  function checkFileExcelExists() {
    const tableSource = coObject.source as TgkTableObjectSource;
    if (isEdit && tableSource?.objectSource == SourceTypeEnum.EXCEL) {
      fetchFileExist(coObject.documentId, tableSource.form.code).then((resp) => {
        if (resp?.path == "") {
          setIsFileExcelExisting(false);
        } else {
          tableSource.form.oidPath = resp?.path?.split(",");
          setFileExcelName(resp?.fileName);
          setIsFileExcelExisting(true);
        }
        setOriginalSheetArea(tableSource.sheet);
      });
    }
    return isFileExcelExisting;
  }

  const [inheritableParameters, setInheritableParameters] = useState<DimensionDetail[]>();

  useEffect(() => {
    (async () => {
      const dbId = (coObject?.source as any)?.dbApplication?.id;
      if (!dbId || !documentPartMetadata.parametersMetadata) {
        setInheritableParameters(undefined);
        return;
      }
      const documentParams = documentPartMetadata.parametersMetadata?.find((p) => p.dbId === dbId)?.dimensions;
      setInheritableParameters(documentParams);
    })();
  }, [(coObject?.source as any)?.dbApplication?.id, documentPartMetadata.parametersMetadata]);

  // keyed scenarioCode or undefined for dimensions
  const [referringScenarios, setReferringScenarios] = useState<Scenario | null>();

  useEffect(() => {
    let active = true;
    (async () => {
      const source = coObject?.source as any;
      const dbId = source?.dbApplication?.id;
      const inheritableParameters = dbId
        ? documentPartMetadata.parametersMetadata?.find((p) => p.dbId === dbId)?.dimensions
        : undefined;

      const referringScenarios = await fetchReferringScenario(coObject, documentPartMetadata, isEdit);
      if (!active) return;
      setReferringScenarios(referringScenarios);

      // The "inherit more than one value" rule takes precedence over the referring-scenario
      // errors: adding a second scenario value to the document breaks both at once, so we
      // show only the more informative morethanonevalue error and suppress the (now redundant)
      // referring-scenario notification.
      if (!isFormAmount || formParametersMetadata !== undefined) {
        const checkInheritedValues =
          isFormAmount && !isEdit ? hasInheritMoreThanOneValueViolation : notifyInheritMoreThanOneValue;
        const inheritsMoreThanOneValue = checkInheritedValues(coObject, inheritableParameters, formParametersMetadata);
        if (!inheritsMoreThanOneValue) {
          notifyInvalidReferenceScenario(coObject, inheritableParameters, referringScenarios);
        }
      }

      const validateObject = validate(undefined, false, false, coObject, referringScenarios);
      props.saveCoObject(coObject, validateObject.validator);
    })();
    return () => {
      active = false;
    };
  }, [inheritableParameters, formParametersMetadata, isFormAmount]);

  async function getParametersMetadata(
    objectSource: TgkTableObjectSource | TgkAmountObjectSource
  ): Promise<AvailableParameters> {
    if (objectSource.objectSource === SourceTypeEnum.EXCEL) return;
    if (!objectSource?.form?.code) {
      return {
        mandatoryParametersMetadata: [],
        optionalParametersMetadata: [],
      };
    }
    LoadingManager.create(TABLE_OBJECT_PARAMETERS);
    try {
      const parametersMetadata = await fetchParametersMetadata(objectSource.dbApplication.id, objectSource.form.code);
      const mandatoryParametersMetadata = parametersMetadata.filter((dimension) => dimension.mandatory);
      const userSelectedParametersMetadata = parametersMetadata.filter((dimension) => !dimension.mandatory);
      const inheritableParametersMap = parametersMetadata.reduce<Map<string, TgkParameterMetadata>>((acc, p) => {
        if (acc.has(p.dimensionCode)) acc.set(p.dimensionCode, null);
        else acc.set(p.dimensionCode, p);
        return acc;
      }, new Map());

      if (!isEdit && !isComponentInstance(coObject) && !pasteMode) {
        const thisDimensionsInheritableParameters = (inheritableParameters || [])
          .map((d) => {
            const parameter = inheritableParametersMap.get(d.structureCode);
            if (parameter) return { ...d, code: parameter.code, inherited: true };
            return null;
          })
          .filter(Boolean);
        const newCoObject = {
          ...coObject,
          parameters: thisDimensionsInheritableParameters,
        };
        props.saveCoObject(newCoObject, validator);
      }

      if (isEdit && !pasteMode) {
        notifyIfMissingParameters(coObject, mandatoryParametersMetadata);
      }
      LoadingManager.remove(TABLE_OBJECT_PARAMETERS);

      return {
        mandatoryParametersMetadata: mandatoryParametersMetadata,
        optionalParametersMetadata: userSelectedParametersMetadata,
      };
    } catch {
      LoadingManager.remove(TABLE_OBJECT_PARAMETERS);
    }
  }

  useEffect(() => {
    let active = true;
    const source = props.coObject.source as TgkTableObjectSource;
    if (!pasteMode)
      getParametersMetadata(source).then((meta) => {
        if (meta && active)
          setAvailableParametersMetadata({
            ...meta,
            dbId: source?.dbApplication?.id,
            formCode: source?.form?.code,
          });
      });

    return () => {
      active = false;
      LoadingManager.remove(TABLE_OBJECT_PARAMETERS);
    };
  }, [(props.coObject.source as TgkTableObjectSource)?.form, amountSource?.dbApplication?.id, pasteMode]);

  useEffect(() => {
    const objSource = props.coObject.source as TgkAmountObjectSource;
    if (objSource?.dimensions && objSource.dbApplication?.id)
      fetchDimensions(objSource.dbApplication.id).then((dimensions) => {
        const mandatoryDimensionsMetadata = dimensions.filter((dimension) => dimension.mandatory);
        const optionalDimensionsMetadata = dimensions.filter((dimension) => !dimension.mandatory);
        setAvailableDimensionsMetadata({ mandatoryDimensionsMetadata, optionalDimensionsMetadata });
      });
  }, [(props.coObject.source as TgkAmountObjectSource)?.dbApplication]);

  async function getDatasetFields() {
    const objectSource = props.coObject.source as TgkTextSource | TgkAmountObjectSource;
    if (!objectSource?.dataset?.code) {
      setDatasetFields([]);
      return;
    }
    LoadingManager.create(TGK_TEXT_OBJECT_DATASET_FIELDS);
    const datasetFields = await fetchDatasetFields(
      objectSource.dbApplication.id.toString(),
      objectSource.analyticalWorkspace.code.toString(),
      objectSource.dataset.code.toString(),
      objectSource.dataset.partitionType?.toString(),
      descriptionLanguage()
    );
    setDatasetFields(datasetFields);
    LoadingManager.remove(TGK_TEXT_OBJECT_DATASET_FIELDS);
  }

  useEffect(() => {
    if (props.coObject?.info?.objectType === "TGK_TEXT" || props.coObject?.source?.type === "AMOUNT_AIH") {
      void getDatasetFields();
    }
    return () => LoadingManager.remove(TGK_TEXT_OBJECT_DATASET_FIELDS);
  }, [(props.coObject.source as TgkTextSource | TgkAmountObjectSource)?.dataset]);

  function getBreadcrumbTitle() {
    if (selectedTab) return `${formatMessage(selectedTab.name)} (${selectedKey + 1}/${tabs ? tabs.length : 1})`;
    return "";
  }

  function onClickPivot(selected: { props: { itemKey: any } }) {
    if (isEdit || isComponentInstance(coObject) || Number(selected.props.itemKey) < selectedKey) {
      setState((s) => ({ ...s, selectedKey: Number(selected.props.itemKey), tableUpdateType: "update" }));
    }
  }

  function validate(
    optionalTabValidation: ObjectPanelTabs | undefined,
    modifiedFields: boolean,
    modifiedSource: boolean,
    newCoObject: CoObjectItem,
    referringScenarios: Scenario
  ) {
    let isAllValid = true;
    let tabsNew: ObjectPanelTabs[];
    if (isComponentInstance(newCoObject)) {
      tabsNew = initTabsByType(newCoObject.info.objectType, false, newCoObject);
      return {
        tabs: tabsNew,
        validator: validateComponentData(newCoObject, modifiedFields, modifiedSource),
      };
    } else {
      tabsNew =
        newCoObject.info && newCoObject.info.objectType != coObject.info?.objectType
          ? initTabsByType(newCoObject.info.objectType, false, newCoObject)
          : state.tabs;
      tabsNew.forEach((t) => {
        if (t.key == optionalTabValidation?.key) {
          t.isValid = optionalTabValidation.isValid;
        }
        if (!t.isValid) isAllValid = false;
      });

      if (!validateRefScenario(newCoObject, referringScenarios)) isAllValid = false;

      const newSource = newCoObject.source as TgkAmountObjectSource;
      const metadata =
        newSource?.dbApplication?.id === availableParametersMetadata.dbId &&
        newSource?.form?.code === availableParametersMetadata.formCode
          ? formParametersMetadata
          : undefined;
      if (hasInheritMoreThanOneValueViolation(newCoObject, inheritableParameters, metadata)) {
        isAllValid = false;
      }

      const newValidator: ObjectPanelValidator = {
        isAllValid: isAllValid,
        modifiedFields: modifiedFields || validator.modifiedFields,
        modifiedSource: modifiedSource || state.sourceEdited,
      };

      return { tabs: tabsNew, validator: newValidator };
    }
  }

  async function updateWithComponentData(component: ComponentReference): Promise<void> {
    const inheritedCoObject: CoObjectItem = await applyComponentData(
      {
        ...coObject,
        info: {
          ...coObject.info,
          objectType: component.type,
          componentReference: {
            documentId: component.documentId,
            documentName: component.documentName,
            contentControlId: component.contentControlId,
            objectName: component.objectName,
          } as ComponentReference,
        },
      },
      documentPartMetadata
    );

    props.saveCoObject(inheritedCoObject, validateComponentData(inheritedCoObject, false, true));
    setState((s) => ({
      ...s,
      tabs: initTabsByType(inheritedCoObject.info.objectType, false, inheritedCoObject),
    }));
  }

  function updateObjectInfo(
    objInfo: CoObjectGeneralInfo,
    tabValidator: ObjectPanelTabs | undefined,
    modifiedFields: boolean,
    modifiedType?: boolean,
    modifiedSources?: boolean
  ) {
    const newCoObject = {
      ...coObject,
      info: objInfo,
      source: modifiedType ? null : coObject.source,
      advanced: modifiedType ? null : coObject.advanced,
    };

    setState((s) => {
      const validateObject = validate(
        tabValidator,
        modifiedFields,
        modifiedSources || s.sourceEdited,
        newCoObject,
        referringScenarios
      );
      props.saveCoObject(newCoObject, validateObject.validator);
      return {
        ...s,
        tabs: validateObject.tabs,
        sourceEdited: modifiedSources || s.sourceEdited,
        tableUpdateType: "update",
      };
    });
  }

  const manageTabs = (objSource: { type: string; objectSource: SourceTypeEnum }) => {
    switch (objSource?.type) {
      case "TABLE":
      case "CHART":
        if (objSource.objectSource === SourceTypeEnum.EXCEL && tabs.length !== 3) {
          updateTabs([{ key: "2", name: "cof.createnewobject.advanced", isValid: isEdit }]);
        } else if (objSource.objectSource !== SourceTypeEnum.EXCEL && tabs.length === 3) {
          updateTabs([
            { key: "2", name: "cof.createnewobject.parameters", isValid: isEdit },
            { key: "3", name: "cof.createnewobject.advanced", isValid: isEdit },
          ]);
        }
        break;
      case "AMOUNT":
      case "AMOUNT_AIH":
        if (
          (objSource.objectSource === SourceTypeEnum.TAGETIK_FORM && tabs.length !== 4) ||
          objSource.type === "AMOUNT_AIH"
        ) {
          updateTabs([
            { key: "2", name: "cof.createnewobject.parameters", isValid: isEdit },
            { key: "3", name: "cof.createnewobject.advanced", isValid: isEdit },
          ]);
        } else if (objSource.objectSource !== SourceTypeEnum.TAGETIK_FORM && tabs.length === 4) {
          updateTabs([{ key: "2", name: "cof.createnewobject.advanced", isValid: isEdit }]);
        }
        break;
    }
  };

  const updateTabs = (tabConfig: any[]) => {
    tabs.splice(2);
    tabConfig.forEach((config) => tabs.push(config));
  };

  function updateFileExcelName(newFileNameExcelName: string) {
    setFileExcelName(newFileNameExcelName);
    setIsFileExcelExisting(true);
  }

  function updateObjectSource(objSource, tabValidator: ObjectPanelTabs, modifiedFields: boolean) {
    console.log({ objSource });
    manageTabs(objSource);
    let newSource;
    const newCoObject = { ...coObject };
    switch (objSource?.type) {
      case "TABLE":
      case "CHART":
        newSource = {
          objectSource: objSource.objectSource,
          dbApplication: objSource.dbApplication,
          form: objSource.form,
          sheet: objSource.sheet,
          area: objSource.area,
          chart: objSource.chart,
          oidPath: objSource.path,
          type: objSource.type,
          path: objSource.path,
          elabResultType: objSource.elabResultType,
          inheritFormatImage: objSource.inheritFormatImage,
          formatImage: objSource.formatImage,
        } as TgkTableObjectSource;

        if ((coObject?.source as TgkTableObjectSource)?.form?.code !== newSource?.form?.code)
          newCoObject.parameters = [];
        if (modifiedFields || coObject.source?.elabResultType === "IMAGE_STRINGBASE64") {
          newCoObject.info.overrideData = false;
        }
        if (modifiedFields && coObject.source?.elabResultType !== "IMAGE_STRINGBASE64") {
          newCoObject.info.overrideStyle = false;
        }
        break;
      case "AMOUNT":
      case "AMOUNT_AIH":
        console.log("BEGIN MAP", { objSource });
        newSource = {
          type: objSource.type,
          form: objSource.form,
          sheet: objSource.sheet,
          area: objSource.area,
          cell: objSource.cell,
          objectSource: objSource.objectSource,
          dbApplication: objSource.dbApplication,
          dimensions: objSource.dimensions,
          analyticalWorkspace: objSource.analyticalWorkspace,
          dataset: objSource.dataset,
          amount: objSource.amount,
          process: objSource.process,
        } as TgkAmountObjectSource;

        console.log({ coObject, newSource });
        break;
      case "DROPDOWN_LIST":
        newSource = {
          type: "DROPDOWN_LIST",
          dropDownList: objSource.dropDownList,
        } as TgkDropDownSource;
        break;
      case "DIMENSION_ATTRIBUTE":
        newSource = {
          type: "DIMENSION_ATTRIBUTE",
          dbApplication: objSource.dbApplication,
          dimensions: objSource.dimensions,
          language: objSource.language,
        } as TgkDimensionAttributeSource;
        break;
      case "TGK_TEXT":
        newSource = {
          type: "TGK_TEXT",
          dbApplication: objSource.dbApplication,
          analyticalWorkspace: objSource.analyticalWorkspace,
          dataset: objSource.dataset,
          richText: objSource.richText,
          process: objSource.process,
          dimensions: objSource.dimensions,
        } as TgkTextSource;
        break;
      default:
        newSource = {};
        break;
    }

    newCoObject.source = newSource;
    setState((s) => {
      const validateObject = validate(
        tabValidator,
        modifiedFields,
        modifiedFields || s.sourceEdited,
        newCoObject,
        referringScenarios
      );
      props.saveCoObject(newCoObject, validateObject.validator);
      return { ...s, tabs: validateObject.tabs, sourceEdited: modifiedFields || s.sourceEdited };
    });
  }

  function updateObjectAdvanced(objAdvanced: CoObjectAdvanced, tabValidator: ObjectPanelTabs, modifiedFields: boolean) {
    const newCoObject = {
      ...coObject,
      advanced: objAdvanced,
    };
    const validateObject = validate(tabValidator, modifiedFields, true, newCoObject, referringScenarios);

    props.saveCoObject(newCoObject, validateObject.validator);
    setState((s) => ({
      ...s,
      tabs: validateObject.tabs,
    }));
  }

  function updateObjectParameters(
    objParameters: DimensionDetail[],
    tabValidator: ObjectPanelTabs,
    modifiedFields: boolean
  ) {
    const newCoObject = {
      ...coObject,
      parameters: objParameters,
    };
    const validateObject = validate(tabValidator, modifiedFields, true, newCoObject, referringScenarios);

    props.saveCoObject(newCoObject, validateObject.validator);
    setState((s) => ({
      ...s,
      tabs: validateObject.tabs,
    }));
  }

  async function validateAndUpdateSmartObject(smartObject: CoObjectItem) {
    const isValidTable = coObject.info.objectType === "TABLE" && !(smartObject.source as any).chart?.name?.length;
    const isValidChart = coObject.info.objectType === "CHART" && !!(smartObject.source as any).chart?.name;
    const isValidAmount = coObject.info.objectType === "AMOUNT" && !!(smartObject.source as any).area?.name;

    if (!isValidTable && !isValidChart && !isValidAmount) {
      NotificationManager.addNotification({
        title: "",
        message: formatMessage("cof.pasteobject.message.error.cannotpaste.wrongtype"),
        type: "error",
      });
      return;
    }

    await applyInheritedInfoOnSmartObject(smartObject, documentPartMetadata);
    await updateSmartObject(smartObject);
  }

  async function updateSmartObject(smartObject: CoObjectItem) {
    const newCoObject = { ...coObject };
    newCoObject.source = smartObject.source;

    newCoObject.parameters = (smartObject.parameters as DimensionDetail[])?.filter(
      (parameter) => Array.isArray(parameter.values) && parameter.values.length > 0
    );

    newCoObject.advanced = updateSmartObjectAdvanced(newCoObject.advanced, isEdit, documentPartMetadata);

    const formParameters = await getParametersMetadata(
      newCoObject.source as TgkTableObjectSource | TgkAmountObjectSource
    );
    if (formParameters) {
      const source = newCoObject.source as TgkAmountObjectSource;
      setAvailableParametersMetadata({
        ...formParameters,
        dbId: source?.dbApplication?.id,
        formCode: source?.form?.code,
      });
    }

    const tabsValid = state.tabs.map((value, index) => ({
      ...value,
      isValid: index !== 2 || checkParametersValidity(newCoObject, formParameters ?? availableParametersMetadata),
    }));

    const validator: ObjectPanelValidator = {
      isAllValid: tabsValid.reduce((acc, tab) => tab.isValid && acc, true),
      modifiedFields: true,
      modifiedSource: true,
    };

    setPasteMode(true);
    setState((s) => ({
      ...s,
      sourceEdited: true,
      tabs: tabsValid,
    }));

    props.saveCoObject(newCoObject, validator);
  }

  const applyInheritedInfoOnSmartObject = async (
    smartObject: CoObjectItem,
    documentPartMetadata: DocumentPartMetadata
  ) => {
    const parameters: DimensionDetail[] = Array.isArray(smartObject?.parameters)
      ? (smartObject.parameters as DimensionDetail[])
      : [];

    const parametersWithoutValues: DimensionDetail[] = parameters.filter(
      (parameter) => Array.isArray(parameter.values) && parameter.values.length === 0
    );

    // get inheritable parameters
    let source = smartObject?.source as any;
    let dbId = source?.dbApplication?.id;

    const inheritableParameters: DimensionDetail[] = (() => {
      if (dbId) {
        const dims = documentPartMetadata?.parametersMetadata?.find((p) => p.dbId === dbId)?.dimensions;
        return Array.isArray(dims) ? dims : [];
      } else {
        return [];
      }
    })();

    // set inherited to parameters without values that can inherit
    parametersWithoutValues.forEach((parameter) => {
      if (inheritableParameters.some((ip) => ip.structureCode === parameter.structureCode)) {
        parameter.inherited = true;
      }
    });

    await applyInheritedInfo(smartObject, documentPartMetadata);
  };

  const updateSmartObjectAdvanced = (
    advanced: CoObjectAdvanced,
    isEdit: boolean,
    documentPartMetadata: DocumentPartMetadata
  ): CoObjectAdvanced => {
    if (isEdit) {
      return advanced;
    }

    return {
      choice: "SOURCE",
      inheritFormat: false,
      language: documentPartMetadata?.language,
    };
  };

  function onNavigate(direction: "NEXT" | "PREV") {
    if (direction === "NEXT" && selectedKey < tabs.length) {
      setState((s) => ({ ...s, selectedKey: selectedKey + 1, tableUpdateType: "update" }));
    } else if (direction === "PREV" && selectedKey > 0) {
      setState((s) => ({ ...s, selectedKey: selectedKey - 1, tableUpdateType: "update" }));
    }
  }

  function goToTabIndex(index: number) {
    setState((s) => ({ ...s, selectedKey: index, tableUpdateType: "update" }));
  }

  function checkNextBtnVisible() {
    return !isEdit && selectedKey < (tabs?.length ?? 1) - 1 && !isComponentInstance(coObject);
  }

  function checkNextBtnEnabled() {
    return !isEdit && tabs[selectedKey]?.isValid && tabs && selectedKey < tabs.length - 1;
  }

  function checkInsertBtnVisible() {
    return !isEdit && tabs && (selectedKey == tabs.length - 1 || isComponentInstance(coObject) || pasteMode === true);
  }

  function checkInsertBtnEnabled() {
    return (
      !isEdit &&
      tabs &&
      (selectedKey == tabs.length - 1 || isComponentInstance(coObject) || pasteMode) &&
      validator.isAllValid &&
      validator.modifiedFields &&
      checkParametersValidity(coObject, availableParametersMetadata)
    );
  }

  function checkSaveBtnVisible() {
    return isEdit;
  }

  const checkMandatoryDimensions = (coObject: CoObjectItem, availableDimensionsMetadata: AvailableDimensions) => {
    if (!coObject?.source) return true;

    const source = coObject?.source as { dimensions?: DimensionDetail[] };
    const objectDimensions = source?.dimensions;

    if (!objectDimensions || coObjectIsWithoutDimensions(coObject)) {
      return true;
    }

    let mandatoryDimensions: Dimension[] = availableDimensionsMetadata.mandatoryDimensionsMetadata;

    switch (coObject.info?.objectType) {
      case CoObjectTypeEnum.DIMENSION_ATTRIBUTE:
        return objectDimensions.length === 1 && objectDimensions[0].values?.length === 1;
      case CoObjectTypeEnum.AMOUNT:
        return true;
      case CoObjectTypeEnum.AMOUNT_AIH:
      case CoObjectTypeEnum.TGK_TEXT:
        mandatoryDimensions = datasetFields ? extractMandatoryDimensionsFromDatasetFields(datasetFields) : [];
        break;
      default:
        break;
    }

    if (!mandatoryDimensions || mandatoryDimensions.length === 0) {
      return true;
    }

    return mandatoryDimensions.every((mandatoryDimensionRoot) =>
      objectDimensions?.some((d) => d.dimensionOid === mandatoryDimensionRoot.oid && d.values?.length)
    );
  };

  const checkParametersValidity = (coObject: CoObjectItem, availableParametersMetadata: AvailableParameters) => {
    if (coObject?.info?.componentReference != undefined) return true;
    if (coObject?.info && coObjectIsWithoutParameters(coObject)) return true;
    if (
      !availableParametersMetadata.mandatoryParametersMetadata ||
      !availableParametersMetadata.optionalParametersMetadata
    )
      return false;
    const objectParameters = coObject.parameters as DimensionDetail[];
    for (const mandatoryParameterRoot of availableParametersMetadata.mandatoryParametersMetadata) {
      if (mandatoryParameterRoot.type === "dimension") {
        const thisParameterValue = objectParameters.find(
          (d) => d.structureCode === mandatoryParameterRoot.dimensionCode && d.code === mandatoryParameterRoot.code
        );
        if (!thisParameterValue?.values?.length) return false;
      } else {
        const thisParameterValue = objectParameters.find((d) => d.dimensionOid === mandatoryParameterRoot.oid);
        if (!thisParameterValue?.values[0]?.desc?.toString()?.length) return false;
      }
    }
    return true;
  };

  function checkSaveBtnEnabled() {
    return (
      isEdit &&
      validator.modifiedFields &&
      validator.isAllValid &&
      checkParametersValidity(coObject, availableParametersMetadata) &&
      checkMandatoryDimensions(coObject, availableDimensionsMetadata) &&
      coObject?.info?.objectType != "DOCUMENT_PART_INCLUDE" &&
      isFeatureActiveOnCurrentClient()
    );
  }

  function checkUpdateBtnEnabled() {
    return (
      isEdit &&
      !validator.modifiedFields &&
      validator.isAllValid &&
      checkParametersValidity(coObject, availableParametersMetadata) &&
      checkMandatoryDimensions(coObject, availableDimensionsMetadata) &&
      isFeatureActiveOnCurrentClient()
    );
  }

  function checkUpdateBtnVisible() {
    return (
      isEdit &&
      coObject.info.objectType !== "CHECKBOX" &&
      coObject.info.objectType !== "DROPDOWN_LIST" &&
      coObject.info.objectType !== "FREE_TEXT"
    );
  }

  function checkDeleteBtnEnabled() {
    return coObject?.info?.objectType != "DOCUMENT_PART_INCLUDE" && isFeatureActiveOnCurrentClient();
  }

  async function saveAndNavigate() {
    if (!isEdit) {
      const isInsideTable = await isCursorInsideTable();
      if (
        (coObject.info.objectType === CoObjectTypeEnum.TABLE || coObject.info.objectType === CoObjectTypeEnum.CHART) &&
        isInsideTable
      ) {
        NotificationManager.addNotification({
          title: "",
          message: formatMessage("cof.createnewobject.save.message.error.innerobject.notpermitted"),
          type: "error",
        });
        return;
      }
      isAddNewPossible().then((res) => {
        if (res) {
          onSaveBtn(state.tableUpdateType);
          setState((s) => ({ ...s, selectedKey: 0, sourceEdited: false, tableUpdateType: "update" }));
          return;
        }
        NotificationManager.addNotification({
          title: "",
          message: formatMessage("cof.createnewobject.save.message.error.innerobject.notpermitted"),
          type: "error",
        });
      });
    } else {
      onSaveBtn(state.tableUpdateType);
      setState((s) => ({ ...s, selectedKey: 0, sourceEdited: false, tableUpdateType: "update" }));
      setIsOldSheetOrAreaSelected(false);
    }
  }

  function updateLockObjectInfo(isLocked: boolean) {
    const newCoObject = {
      ...coObject,
      isLocked: isLocked,
    };
    const validateObject = validate(undefined, false, false, newCoObject, referringScenarios);
    props.saveCoObject(newCoObject, validateObject.validator);
    setState((s) => ({ ...s, tabs: validateObject.tabs, tableUpdateType: "update" }));
  }

  const dispatch = useAppDispatch();

  async function onUnlockObject() {
    LoadingManager.create("lock");
    await lockObject(coObject, false);
    updateLockObjectInfo(false);
    await moveCursorToContentControlId(Number(coObject.contentControlId));
    setTimeout(() => {
      dispatch(navigate(EDIT_OBJECT));
      LoadingManager.remove("lock");
    }, 1000);
  }

  async function refreshClickHandler(tableUpdateType: TableUpdateStrategy["typeUpdate"]) {
    const clientID = store.getState().configurationState.clientId;
    const correlationID = uuid.v4();

    try {
      let elaboration: ElaborationRequest = {
        correlationId: correlationID,
        clientId: clientID,
        documentId: `${coObject.documentId}`,
        userId: username,
        tgkElaborationItemList: [],
      };

      const item = await mapCoObjectToRefreshElaborationItem(
        correlationID,
        documentPart,
        documentPartMetadata,
        coObject,
        undefined,
        tableUpdateType,
        fileExcelName
      );
      elaboration.tgkElaborationItemList.push(item);

      if (elaboration.tgkElaborationItemList.length) {
        //if no elaboration present, if the item generated an error while updating the inherited formatting
        const elaborationResponse = await createElaborationRequestAsync(elaboration);

        console.log("elaborationResponse: ", elaborationResponse);

        if (!isComponentInstance(coObject)) {
          editTitleAndTag(coObject);
        }
      }
    } catch (error) {
      console.error(error);
      NotificationManager.addNotification({
        key: EDIT_OBJECT_NOTIFICATION,
        message:
          formatMessageDefault("cof.elaboration.refresh.message.error", "Error updating object: ") + error.message,
        title: formatMessageDefault("cof.elaboration.refresh.title", "Object update"),
        type: "error",
        closable: true,
        removeOnClose: true,
        isTemporary: false,
      });
    }
  }

  const [deleted, setDeleted] = useState<boolean>(false);
  const [modalProps, setModalProps] = useState<CustomModalProps | undefined>(undefined);

  const getHeaderTitle = () => {
    if (deleted)
      return formatMessageWithParams("cof.createnewobject.modal.delete", {
        coObjectName: coObject.info.objectName,
      });
    if (coObject.id)
      return formatMessageWithParams("cof.createnewobject.modal.edit", {
        coObjectName: coObject.info.objectName,
      });
    return selectedKey > 0 && coObject.info?.objectName
      ? formatMessage("cof.createnewobject.modal.new") + " - " + coObject.info.objectName
      : formatMessage("cof.createnewobject.modal.new");
  };

  const setDeletionConfirm = () => {
    setModalProps({
      modalTitle: formatMessageWithParams("cof.createnewobject.modal.delete", {
        coObjectName: coObject.info.objectName,
      }),
      messageTitle: formatMessageWithParams("cof.createnewobject.modal.delete.message", {
        coObjectName: coObject.info.objectName,
      }),
      message: null,
      type: "delete",
      onHide: () => {
        return;
      },
      actions: [
        {
          id: "cancel_button",
          title: formatMessage("cof.base.noconfirmdelete"),
          onClick: () => {
            setModalProps(undefined);
          },
          variant: "blue",
        },
        {
          id: "confirm_button",
          title: formatMessage("cof.base.confirmdelete"),
          onClick: () => {
            deleteObjectAsync({ coObject })
              .then(() => {
                setDeleted(true);
                setModalProps(undefined);
                // Force ValidationPanelHook to reload tags/coObjects so the Tags Panel
                // does not keep rendering the just-deleted object (IDS-6546 follow-up).
                dispatch(invalidate());
              })
              .catch(() => {
                setDeleted(undefined);
                setModalProps(undefined);
              });
          },
          variant: "red_filled",
        },
      ],
    });
  };

  const setTableUpdateType = (tableUpdateType: TableUpdateStrategy["typeUpdate"]) => {
    setState((s) => ({ ...s, tableUpdateType }));
  };

  return {
    tableUpdateType: state.tableUpdateType,
    getHeaderTitle,
    modalProps,
    deleted,
    isEdit,
    updateWithComponentData,
    selectedKey,
    onClickPivot,
    tabs,
    getBreadcrumbTitle,
    coObject,
    documentPartMetadata,
    updateObjectInfo,
    updateObjectSource,
    updateObjectAdvanced,
    updateObjectParameters,
    validateAndUpdateSmartObject,
    inheritableParametersInThisDb: inheritableParameters,
    availableParametersMetadata,
    datasetFields,
    saveAndNavigate,
    onUnlockObject,
    updateLockObjectInfo,
    onNavigate,
    checkInsertBtnEnabled,
    checkNextBtnVisible,
    checkUpdateBtnEnabled,
    checkUpdateBtnVisible,
    setDeletionConfirm,
    refreshClickHandler,
    checkInsertBtnVisible,
    checkNextBtnEnabled,
    checkSaveBtnEnabled,
    checkSaveBtnVisible,
    checkDeleteBtnEnabled,
    isLocked,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    setTableUpdateType,
    goToTabIndex,
    originalSheetArea,
    setOriginalSheetArea,
    isOldSheetOrAreaSelected,
    setIsOldSheetOrAreaSelected,
    isLoadingSheetAndArea,
    setIsLoadingSheetAndArea,
    setIsSheetChartAndAreaToHide,
    isSheetChartAndAreaToHide,
    referringScenarios,
    availableDimensionsMetadata,
  };
};
export default useObjectPanelComponent;
